import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp
} from "firebase/firestore";
import { db, auth } from "./firebase";

// Bootstrap default super-admin emails
export const SUPER_ADMIN_EMAILS = [
  "anakhar.22@gmail.com",
  "anakhar022@gmail.com",
  "pranay@erayaha.ai",
  "ahalyar11@gmail.com"
];

export const SUPER_ADMIN_EMAIL = SUPER_ADMIN_EMAILS[0]; // backward compatibility

export function isSuperAdminEmail(email) {
  if (!email) return false;
  const normalized = email.toLowerCase().trim();
  return SUPER_ADMIN_EMAILS.some((admin) => admin.toLowerCase().trim() === normalized);
}

/**
 * Check whether an email has administrative privileges.
 * Always returns true for bootstrap super-admins.
 */
export async function checkIsAdmin(email) {
  if (!email) return false;
  const normalizedEmail = email.toLowerCase().trim();
  if (isSuperAdminEmail(normalizedEmail)) {
    return true;
  }

  try {
    const adminDocRef = doc(db, "admins", normalizedEmail);
    const snap = await getDoc(adminDocRef);
    return snap && typeof snap.exists === "function" ? snap.exists() : false;
  } catch (err) {
    console.warn("[Firestore] Error checking admin status:", err);
    return false;
  }
}

// ----------------------------------------------------
// Admin Management
// ----------------------------------------------------

export async function getAdminsList() {
  const admins = SUPER_ADMIN_EMAILS.map((email) => ({
    email,
    role: "super_admin",
    created_at: "System Default"
  }));
  try {
    const snap = await getDocs(collection(db, "admins"));
    if (snap && typeof snap.forEach === "function") {
      snap.forEach((docSnap) => {
        const data = typeof docSnap.data === "function" ? docSnap.data() : (docSnap || {});
        const email = docSnap.id || data.email;
        if (email && !isSuperAdminEmail(email)) {
          admins.push({
            email: email,
            role: "admin",
            created_at: data.created_at || new Date().toISOString()
          });
        }
      });
    }
  } catch (err) {
    console.warn("[Firestore] Failed to fetch admins collection:", err);
  }
  return admins;
}

export async function addAdminEmail(email) {
  if (!email) throw new Error("Email is required");
  const normalized = email.toLowerCase().trim();
  const docRef = doc(db, "admins", normalized);
  await setDoc(docRef, {
    email: normalized,
    created_at: new Date().toISOString()
  });
  return { email: normalized, role: "admin" };
}

export async function removeAdminEmail(email) {
  if (!email) throw new Error("Email is required");
  const normalized = email.toLowerCase().trim();
  if (isSuperAdminEmail(normalized)) {
    throw new Error("Cannot remove a bootstrap super-admin.");
  }
  const docRef = doc(db, "admins", normalized);
  await deleteDoc(docRef);
  return true;
}

// ----------------------------------------------------
// Custom Drugs & Global Presets
// ----------------------------------------------------

export async function getCustomDrugs(userId) {
  const drugs = [];
  const drugMap = new Map();

  try {
    // 1. Fetch global drugs
    const globalQuery = query(collection(db, "custom_drugs"), where("user_id", "==", "global"));
    const globalSnap = await getDocs(globalQuery);
    globalSnap.forEach((docSnap) => {
      const item = { id: docSnap.id, ...docSnap.data() };
      drugMap.set(docSnap.id, item);
    });

    // 2. Fetch user's own custom drugs if signed in
    if (userId && userId !== "global") {
      const userQuery = query(collection(db, "custom_drugs"), where("user_id", "==", userId));
      const userSnap = await getDocs(userQuery);
      userSnap.forEach((docSnap) => {
        const item = { id: docSnap.id, ...docSnap.data() };
        drugMap.set(docSnap.id, item);
      });
    }

    drugMap.forEach((val) => drugs.push(val));
  } catch (err) {
    console.warn("[Firestore] Failed to load custom drugs from Firestore, checking localStorage:", err);
    try {
      const local = localStorage.getItem("driplee_custom_drugs");
      if (local) return JSON.parse(local);
    } catch {}
  }

  return drugs;
}

export async function saveCustomDrug(drugData, userId, isGlobal = false) {
  const effectiveUserId = isGlobal ? "global" : (userId || auth.currentUser?.uid || "anonymous");
  
  const payload = {
    user_id: effectiveUserId,
    category_id: drugData.category_id || "inotropes",
    name: drugData.name.trim(),
    dose_unit: drugData.dose_unit || "mcg/kg/min",
    default_drug_amount: Number(drugData.default_drug_amount),
    default_drug_amount_unit: drugData.default_drug_amount_unit || "mg",
    default_volume: Number(drugData.default_volume),
    default_dose: Number(drugData.default_dose),
    range_min: Number(drugData.range_min),
    range_max: Number(drugData.range_max),
    note: drugData.note || "",
    reference_source: drugData.reference_source || "",
    reference_url: drugData.reference_url || "",
    created_at: new Date().toISOString()
  };

  if (drugData.id) {
    const docRef = doc(db, "custom_drugs", drugData.id);
    await setDoc(docRef, payload, { merge: true });
    return { id: drugData.id, ...payload };
  } else {
    const docRef = await addDoc(collection(db, "custom_drugs"), payload);
    return { id: docRef.id, ...payload };
  }
}

export async function deleteCustomDrug(drugId) {
  if (!drugId) throw new Error("Drug ID is required");
  const docRef = doc(db, "custom_drugs", drugId);
  await deleteDoc(docRef);
  return true;
}

// ----------------------------------------------------
// Calculator Metadata CMS
// ----------------------------------------------------

export async function getCalculatorsMetadata() {
  const calcs = [];
  try {
    const snap = await getDocs(collection(db, "calculators"));
    snap.forEach((docSnap) => {
      calcs.push({ id: docSnap.id, ...docSnap.data() });
    });
  } catch (err) {
    console.warn("[Firestore] Failed to fetch calculators metadata:", err);
  }
  return calcs;
}

export async function saveCalculatorMetadata(calcData) {
  if (!calcData.id) throw new Error("Calculator ID is required");
  const docRef = doc(db, "calculators", calcData.id);
  const payload = {
    id: calcData.id,
    name: calcData.name || "",
    category: calcData.category || "General",
    slug: calcData.slug || calcData.id,
    description: calcData.description || "",
    icon: calcData.icon || "Calculator",
    formula_version: calcData.formula_version || "1.0.0",
    status: calcData.status || "active",
    display_order: Number(calcData.display_order || 10),
    seo_title: calcData.seo_title || "",
    meta_description: calcData.meta_description || "",
    formula_explanation: calcData.formula_explanation || "",
    worked_example: calcData.worked_example || "",
    clinical_notes: calcData.clinical_notes || "",
    references: calcData.references || "",
    disclaimer: calcData.disclaimer || "",
    keywords: calcData.keywords || "",
    updated_at: new Date().toISOString()
  };
  await setDoc(docRef, payload, { merge: true });
  return payload;
}

export async function deleteCalculatorMetadata(calcId) {
  if (!calcId) throw new Error("Calculator ID is required");
  const docRef = doc(db, "calculators", calcId);
  await deleteDoc(docRef);
  return true;
}

// ----------------------------------------------------
// Feedback
// ----------------------------------------------------

export async function submitFeedback(feedbackData) {
  const payload = {
    message: feedbackData.message.trim(),
    rating: feedbackData.rating || null,
    user_id: feedbackData.user_id || auth.currentUser?.uid || "anonymous",
    user_email: feedbackData.user_email || auth.currentUser?.email || "anonymous",
    created_at: new Date().toISOString()
  };

  const docRef = await addDoc(collection(db, "feedbacks"), payload);
  return { id: docRef.id, ...payload };
}

export async function getFeedbacksList() {
  const list = [];
  try {
    const snap = await getDocs(collection(db, "feedbacks"));
    snap.forEach((docSnap) => {
      list.push({ id: docSnap.id, ...docSnap.data() });
    });
    list.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  } catch (err) {
    console.warn("[Firestore] Failed to fetch feedback list:", err);
  }
  return list;
}

export async function deleteFeedbackDoc(feedbackId) {
  if (!feedbackId) throw new Error("Feedback ID is required");
  const docRef = doc(db, "feedbacks", feedbackId);
  await deleteDoc(docRef);
  return true;
}

// ----------------------------------------------------
// Logging & Telemetry
// ----------------------------------------------------

export async function logCalculatorUsage(calculatorId, status = "success", errorMessage = "") {
  try {
    const user = auth.currentUser;
    const payload = {
      calculator_id: calculatorId,
      status,
      error_message: errorMessage || null,
      user_id: user?.uid || "anonymous",
      email: user?.email || "anonymous",
      timestamp: new Date().toISOString()
    };
    await addDoc(collection(db, "calculator_usage_logs"), payload);
  } catch (err) {
    // Non-blocking telemetry
  }
}

export async function logSignInEvent(user) {
  try {
    if (!user) return;
    const payload = {
      user_id: user.uid || user.id,
      email: user.email || "unknown",
      phone_e164: user.phoneNumber || user.phone_e164 || "unknown",
      timestamp: new Date().toISOString()
    };
    await addDoc(collection(db, "sign_in_logs"), payload);
  } catch (err) {
    // Non-blocking telemetry
  }
}

// ----------------------------------------------------
// Dashboard Metrics Aggregator
// ----------------------------------------------------

export async function getAdminDashboardMetrics() {
  const metrics = {
    totalUsers: 1,
    activeSubscribers: 1,
    mrr: 19,
    totalCalculations: 0,
    feedbacksCount: 0,
    customDrugsCount: 0,
    calculatorsCount: 0
  };

  const mostUsed = [];
  const recentSignIns = [];
  const recentUsers = [];

  try {
    // Calculators
    const calcsSnap = await getDocs(collection(db, "calculators"));
    metrics.calculatorsCount = calcsSnap.size;

    // Custom drugs
    const drugsSnap = await getDocs(collection(db, "custom_drugs"));
    metrics.customDrugsCount = drugsSnap.size;

    // Feedbacks
    const feedbackSnap = await getDocs(collection(db, "feedbacks"));
    metrics.feedbacksCount = feedbackSnap.size;

    // Usage logs
    const usageSnap = await getDocs(collection(db, "calculator_usage_logs"));
    metrics.totalCalculations = usageSnap.size;

    const calcCounts = {};
    usageSnap.forEach((docSnap) => {
      const data = docSnap.data();
      const id = data.calculator_id || "general";
      calcCounts[id] = (calcCounts[id] || 0) + 1;
    });

    Object.entries(calcCounts).forEach(([name, count]) => {
      mostUsed.push({ name, count });
    });
    mostUsed.sort((a, b) => b.count - a.count);

    // Sign-in logs
    const signInsSnap = await getDocs(collection(db, "sign_in_logs"));
    signInsSnap.forEach((docSnap) => {
      const data = docSnap.data();
      recentSignIns.push({
        email: data.email || "user",
        timestamp: data.timestamp || new Date().toISOString()
      });
    });
    recentSignIns.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    metrics.totalUsers = Math.max(1, recentSignIns.length);

  } catch (err) {
    console.warn("[Firestore] Metrics fetch encountered error:", err);
  }

  return {
    metrics,
    mostUsedCalculators: mostUsed.slice(0, 5),
    recentSignIns: recentSignIns.slice(0, 5),
    recentUsers
  };
}
